import { telemetryEnabled } from '@mygpt/app/config/integrations';
export const loadScript = (url: string) => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = url;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

/**
 * 第三方分析脚本管理器
 */
export class ThirdPartyScriptsManager {
  private static loadedScripts = new Set<string>();

  /**
   * 加载 Ahrefs Analytics 脚本
   */
  static async loadAhrefsAnalytics() {
    if (!telemetryEnabled) return;
    const scriptId = 'ahrefs-analytics';
    if (this.loadedScripts.has(scriptId)) return;

    try {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://analytics.ahrefs.com/analytics.js';
      script.setAttribute('data-key', 'ki6CyCRAIT0orm4cggYSsw');
      script.async = true;
      document.head.appendChild(script);
      this.loadedScripts.add(scriptId);
    } catch (error) {
      console.warn('Failed to load Ahrefs Analytics:', error);
    }
  }

  /**
   * 加载 Google Analytics 脚本
   */
  static async loadGoogleAnalytics() {
    if (!telemetryEnabled) return;
    const scriptId = 'google-analytics';
    if (this.loadedScripts.has(scriptId)) return;

    try {
      // 加载 gtag.js
      const gtagScript = document.createElement('script');
      gtagScript.id = 'gtag-script';
      gtagScript.src = 'https://www.googletagmanager.com/gtag/js?id=G-VPV4C8445N';
      gtagScript.async = true;
      document.head.appendChild(gtagScript);

      // 配置 Google Analytics
      window.dataLayer = window.dataLayer || [];
      // @ts-ignore
      function gtag() {
        // @ts-ignore
        dataLayer.push(arguments);
      }
      // @ts-ignore
      gtag('js', new Date());
      // @ts-ignore
      gtag('config', 'G-VPV4C8445N');

      this.loadedScripts.add(scriptId);
    } catch (error) {
      console.warn('Failed to load Google Analytics:', error);
    }
  }

  /**
   * 加载 Microsoft Clarity 脚本
   */
  static async loadMicrosoftClarity() {
    if (!telemetryEnabled) return;
    const scriptId = 'microsoft-clarity';
    if (this.loadedScripts.has(scriptId)) return;

    try {
      (function (c: any, l: any, a: any, r: any, i: any, t: any, y: any) {
        c[a] =
          c[a] ||
          function () {
            (c[a].q = c[a].q || []).push(arguments);
          };
        t = l.createElement(r);
        t.async = 1;
        t.src = 'https://www.clarity.ms/tag/' + i;
        y = l.getElementsByTagName(r)[0];
        y.parentNode.insertBefore(t, y);
      })(window, document, 'clarity', 'script', 'mgvnpsaotk');

      this.loadedScripts.add(scriptId);
    } catch (error) {
      console.warn('Failed to load Microsoft Clarity:', error);
    }
  }

  /**
   * 初始化 Sentry 错误监控
   */
  static async initializeSentry() {
    if (!telemetryEnabled) return;
    const scriptId = 'sentry';
    if (this.loadedScripts.has(scriptId)) return;

    try {
      // 动态导入 Sentry 相关模块
      const [{ default: Sentry }, { BrowserTracing }, { beforeSend }] = await Promise.all([
        import('@sentry/react'),
        import('@sentry/tracing'),
        import('@mygpt/app/utils/sentry'),
      ]);

      // 检查环境和配置
      const env = import.meta.env.VITE_STAGE;
      const dsn = import.meta.env.VITE_SENTRY_DSN;

      if (!['pre', 'production', 'staging', 'onprem-main'].includes(env) || !dsn) {
        console.log('Sentry initialization skipped: environment not supported or DSN not available');
        return;
      }

      Sentry.init({
        dsn,
        environment: env,
        integrations: [Sentry.replayIntegration()],
        beforeSend,
        tracesSampleRate: 1.0,
        attachStacktrace: true,
        // Session Replay
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
      });

      this.loadedScripts.add(scriptId);
      console.log('Sentry initialized successfully');
    } catch (error) {
      console.warn('Failed to initialize Sentry:', error);
    }
  }

  /**
   * 初始化 Mixpanel 分析服务
   */
  static async initializeMixpanel() {
    if (!telemetryEnabled) return;
    const scriptId = 'mixpanel';
    if (this.loadedScripts.has(scriptId)) return;

    try {
      // 动态导入 Mixpanel
      const { default: mixpanel } = await import('mixpanel-browser');

      // 获取 Mixpanel Token
      const MIXPANEL_TOKEN = '1c7ce9876de96d036ad15d8c4e55c5b1';

      mixpanel.init(MIXPANEL_TOKEN, { debug: true, ignore_dnt: true });
      this.loadedScripts.add(scriptId);
      console.log('Mixpanel initialized successfully');
    } catch (error) {
      console.warn('Failed to initialize Mixpanel:', error);
    }
  }

  /**
   * 根据配置加载所有第三方脚本
   * @param shouldLoad 是否应该加载第三方脚本（true表示加载，false表示不加载）
   */
  static async loadThirdPartyScripts(shouldLoad: boolean = true) {
    if (!telemetryEnabled) return;
    // 检查是否为本地环境，本地环境不加载分析脚本
    const isLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname);

    if (!shouldLoad || isLocalhost) {
      console.log('Third-party scripts loading skipped:', { shouldLoad, isLocalhost });
      return;
    }

    console.log('Loading third-party analytics scripts and services');

    // 并行加载所有脚本和服务以提高性能
    await Promise.allSettled([
      this.loadAhrefsAnalytics(),
      this.loadGoogleAnalytics(),
      this.loadMicrosoftClarity(),
      this.initializeSentry(),
      this.initializeMixpanel(),
    ]);
  }

  /**
   * 检查脚本是否已加载
   */
  static isScriptLoaded(scriptId: string): boolean {
    return this.loadedScripts.has(scriptId);
  }

  /**
   * 安全地捕获Sentry消息（仅在Sentry已初始化时）
   * @param message 要发送的消息
   * @param level 消息级别
   */
  static safeCaptureMessage(message: string, level: 'error' | 'warning' | 'info' = 'error') {
    if (this.loadedScripts.has('sentry')) {
      try {
        // 动态导入并使用Sentry
        import('@sentry/react').then(({ default: Sentry }) => {
          Sentry.captureMessage(message, level);
        });
      } catch (error) {
        console.warn('Failed to capture Sentry message:', error);
      }
    } else {
      console.log('Sentry not initialized, message not sent:', message);
    }
  }

  /**
   * 安全地捕获Sentry异常（仅在Sentry已初始化时）
   * @param exception 要发送的异常
   */
  static safeCaptureException(exception: Error) {
    if (this.loadedScripts.has('sentry')) {
      try {
        // 动态导入并使用Sentry
        import('@sentry/react').then(({ default: Sentry }) => {
          Sentry.captureException(exception);
        });
      } catch (error) {
        console.warn('Failed to capture Sentry exception:', error);
      }
    } else {
      console.log('Sentry not initialized, exception not sent:', exception);
    }
  }

  /**
   * 安全地调用Mixpanel track（仅在Mixpanel已初始化时）
   * @param eventName 事件名称
   * @param properties 事件属性
   */
  static safeMixpanelTrack(eventName: string, properties?: Record<string, any>) {
    if (this.loadedScripts.has('mixpanel')) {
      try {
        // 动态导入并使用Mixpanel
        import('mixpanel-browser').then(({ default: mixpanel }) => {
          mixpanel.track(eventName, properties);
        });
      } catch (error) {
        console.warn('Failed to track Mixpanel event:', error);
      }
    } else {
      console.log('Mixpanel not initialized, event not tracked:', eventName, properties);
    }
  }

  /**
   * 安全地调用Mixpanel register（仅在Mixpanel已初始化时）
   * @param properties 要注册的属性
   */
  static safeMixpanelRegister(properties: Record<string, any>) {
    if (this.loadedScripts.has('mixpanel')) {
      try {
        // 动态导入并使用Mixpanel
        import('mixpanel-browser').then(({ default: mixpanel }) => {
          mixpanel.register(properties);
        });
      } catch (error) {
        console.warn('Failed to register Mixpanel properties:', error);
      }
    } else {
      console.log('Mixpanel not initialized, properties not registered:', properties);
    }
  }

  /**
   * 安全地获取Mixpanel distinct_id（仅在Mixpanel已初始化时）
   */
  static async safeMixpanelGetDistinctId(): Promise<string | null> {
    if (this.loadedScripts.has('mixpanel')) {
      try {
        const { default: mixpanel } = await import('mixpanel-browser');
        return mixpanel.get_distinct_id();
      } catch (error) {
        console.warn('Failed to get Mixpanel distinct_id:', error);
        return null;
      }
    } else {
      console.log('Mixpanel not initialized, cannot get distinct_id');
      return null;
    }
  }

  /**
   * 安全地获取Mixpanel属性（仅在Mixpanel已初始化时）
   * @param propertyName 属性名称
   */
  static async safeMixpanelGetProperty(propertyName: string): Promise<any> {
    if (this.loadedScripts.has('mixpanel')) {
      try {
        const { default: mixpanel } = await import('mixpanel-browser');
        return mixpanel.get_property(propertyName);
      } catch (error) {
        console.warn('Failed to get Mixpanel property:', error);
        return null;
      }
    } else {
      console.log('Mixpanel not initialized, cannot get property:', propertyName);
      return null;
    }
  }

  /**
   * 安全地发送Mixpanel事件到API（替代navigator.sendBeacon的直接调用）
   * @param eventData 事件数据
   */
  static safeMixpanelSendBeacon(eventData: any) {
    if (this.loadedScripts.has('mixpanel')) {
      try {
        // 使用Mixpanel SDK而不是直接调用API
        this.safeMixpanelTrack(eventData.event, eventData.properties);
      } catch (error) {
        console.warn('Failed to send Mixpanel beacon:', error);
      }
    } else {
      console.log('Mixpanel not initialized, beacon not sent:', eventData);
    }
  }

  /**
   * 重置已加载的脚本记录（主要用于测试）
   */
  static resetLoadedScripts() {
    this.loadedScripts.clear();
  }
}
